import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress } from '@mui/material';
import PaymentSummary from './PaymentMethod/PaymentSummary.react';
import BankTransferDetailsPanel from './BankTransfer/BankTransferDetailsPanel.react';
import BankTransferUpload from './BankTransfer/BankTransferUpload.react';
import Ajax from '@/utils/Ajax';
import BankTransferActions from '@/actions/BankTransferActions';
import BankTransferStore from '@/stores/BankTransferStore';
import BankTransferConstants from '@/constants/BankTransferConstants';

// payload: { id (payment id), booking_id, files }. Nothing is uploaded until submit
const SettlePaymentBankTransfer = ({ booking, profile, chargeCurrency, onSuccess, onFilesChange }) => {
	const { t } = useTranslation();
	const bookingId = booking?.id;

	const [bankDetails, setBankDetails] = useState(null);
	const [files, setFiles] = useState([]);
	const [status, setStatus] = useState(BankTransferStore.getStatus(bookingId));
	const [progress, setProgress] = useState(BankTransferStore.getProgress(bookingId));

	useEffect(() => {
		Ajax.getBankTransferDetails()
			.done(({ bank_details }) => setBankDetails(bank_details))
			.fail((err) => console.error('Error while fetching bank transfer details:', err));
	}, []);

	useEffect(() => {
		function handleStoreChange() {
			setStatus(BankTransferStore.getStatus(bookingId));
			setProgress(BankTransferStore.getProgress(bookingId));
		}

		BankTransferStore.addChangeListener(handleStoreChange);
		return () => BankTransferStore.removeChangeListener(handleStoreChange);
	}, [bookingId]);

	useEffect(() => {
		if (status === BankTransferConstants.STATUS.SUCCESS) {
			onSuccess?.(BankTransferStore.getRequest(bookingId));
		}
	}, [status, bookingId]);

	useEffect(() => {
		onFilesChange?.(files.length > 0);
	}, [files.length]);

	const handleSubmit = () => {
		BankTransferActions.createBankTransferRequest({
			id: _.get(booking, 'payment._id'),
			booking_id: bookingId,
			files,
		});
	};

	const isSubmitting = status === BankTransferConstants.STATUS.BUSY;
	const hasFailed = status === BankTransferConstants.STATUS.FAILED;
	const canSubmit = files.length > 0 && !isSubmitting;

	return (
		<div className="settle-payment-wrap">
			<div className="settle-payment bank-transfer__page">
				<PaymentSummary profile={profile} booking={booking} chargeCurrency={chargeCurrency} />

				<BankTransferDetailsPanel bankDetails={bankDetails} />

				<BankTransferUpload
					files={files}
					onFilesAdded={(newFiles) => setFiles((prev) => [...prev, ...newFiles])}
					onFileRemoved={(index) => setFiles((prev) => prev.filter((_file, i) => i !== index))}
				/>

				{hasFailed && (
					<div className="bank-transfer__error-row">
						<span className="show-error">{t('Bookings.SettlePayment.BankTransferSubmitFailed')}</span>
					</div>
				)}

				<div className="actions">
					<Button
						variant="contained"
						className="bank-transfer__submit-button"
						disabled={!canSubmit}
						onClick={handleSubmit}
						aria-busy={isSubmitting}
						aria-label={isSubmitting ? t('Bookings.SettlePayment.Submitting') : undefined}
					>
						{isSubmitting ? (
							<CircularProgress size={20} color="inherit" variant="determinate" value={Math.round(progress * 100)} />
						) : (
							t('Bookings.SettlePayment.SubmitBankTransferRequest')
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default SettlePaymentBankTransfer;
